import { useState, useRef } from 'react'
import { Upload, Image, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function ImageUpload({ onComplete, setStatus }) {
  const { api, apiRaw } = useAuth()
  const fileRef = useRef(null)
  const bulkRef = useRef(null)
  const [mode, setMode] = useState('single') // 'single' or 'bulk'
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: 0 })
  const [dragOver, setDragOver] = useState(false)

  async function uploadSingleFile(file) {
    const formData = new FormData()
    formData.append('file', file)

    const res = await apiRaw('/images/upload', {
      method: 'POST',
      body: formData,
      // Don't set Content-Type - let browser set multipart boundary
      rawBody: true,
    })

    return res
  }

  async function handleSingleUpload() {
    const file = fileRef.current?.files?.[0]
    if (!file) {
      alert('Please choose an image file')
      return
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, GIF, WebP, SVG)')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be under 10 MB')
      return
    }

    setUploading(true)
    setStatus('Uploading image...')

    try {
      const data = await uploadSingleFile(file)
      if (data.success) {
        setStatus(`✅ Uploaded: ${file.name}`)
        if (fileRef.current) fileRef.current.value = ''
        onComplete(data.result?.id)
      } else {
        setStatus(`Error: ${data.errors?.[0]?.message || 'Upload failed'}`)
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setUploading(false)
    }
  }

  async function handleBulkUpload() {
    const files = bulkRef.current?.files
    if (!files || files.length === 0) {
      alert('Please choose image files')
      return
    }

    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'))
    if (imageFiles.length === 0) {
      alert('No valid image files selected')
      return
    }

    if (imageFiles.some(f => f.size > 10 * 1024 * 1024)) {
      alert('All images must be under 10 MB each')
      return
    }

    setUploading(true)
    const total = imageFiles.length
    let done = 0
    let errors = 0
    setProgress({ done: 0, total, errors: 0 })
    setStatus(`Uploading 0/${total} images...`)

    for (const file of imageFiles) {
      try {
        const data = await uploadSingleFile(file)
        if (data.success) {
          done++
        } else {
          errors++
        }
      } catch {
        errors++
      }
      setProgress({ done: done + errors, total, errors })
      setStatus(`Uploading ${done + errors}/${total}...${errors ? ` (${errors} failed)` : ''}`)
    }

    setStatus(`✅ ${done} image${done !== 1 ? 's' : ''} uploaded${errors ? `, ${errors} failed` : ''}`)
    setUploading(false)
    setProgress({ done: 0, total: 0, errors: 0 })
    if (bulkRef.current) bulkRef.current.value = ''
    onComplete()
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragOver(false)

    const files = e.dataTransfer?.files
    if (!files || files.length === 0) return

    if (mode === 'single') {
      const dt = new DataTransfer()
      dt.items.add(files[0])
      fileRef.current.files = dt.files
      // Auto-upload on drop
      handleSingleUpload()
    } else {
      bulkRef.current.files = files
    }
  }

  return (
    <div className="image-upload">
      <div className="upload-mode-toggle">
        <h3>
          <Upload size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Upload Images
        </h3>
        <div className="mode-tabs">
          <button
            className={mode === 'single' ? 'active' : ''}
            onClick={() => setMode('single')}
          >
            Single
          </button>
          <button
            className={mode === 'bulk' ? 'active' : ''}
            onClick={() => setMode('bulk')}
          >
            Bulk
          </button>
        </div>
      </div>

      <div
        className={`upload-drop-zone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {mode === 'single' ? (
          <div className="upload-form">
            <div className="upload-row">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                disabled={uploading}
              />
              <button onClick={handleSingleUpload} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
            <p className="upload-hint">
              <Image size={14} /> PNG, JPG, GIF, WebP, SVG — Max 10 MB
            </p>
          </div>
        ) : (
          <div className="upload-form">
            <div className="upload-row">
              <input
                ref={bulkRef}
                type="file"
                accept="image/*"
                multiple
                disabled={uploading}
              />
              <button onClick={handleBulkUpload} disabled={uploading}>
                {uploading ? `Uploading ${progress.done}/${progress.total}...` : 'Upload All'}
              </button>
            </div>
            <p className="upload-hint">
              <Image size={14} /> Select multiple images — Max 10 MB each
            </p>

            {uploading && progress.total > 0 && (
              <div className="upload-progress">
                <progress value={progress.done} max={progress.total} />
                <span>{progress.done}/{progress.total}{progress.errors ? ` (${progress.errors} errors)` : ''}</span>
              </div>
            )}
          </div>
        )}

        {dragOver && (
          <div className="drop-overlay">
            <Upload size={48} />
            <span>Drop image{mode === 'bulk' ? 's' : ''} here</span>
          </div>
        )}
      </div>
    </div>
  )
}
